import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Navigation, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CANCEL_REASONS, DRIVER_NEXT, money, navUrl, when } from "@/lib/rides";
import { StatusBadge, StepTrack, Stops } from "@/components/RideStatus";
import { ActionDialog } from "@/components/ActionDialog";
import { Button } from "@/components/ui/button";
import { MessagesButton } from "@/components/RideMessages";
import { SharingBadge, useShareLocation } from "@/components/FollowRide";
import { CounterpartProfile, RiderLocationForDriver } from "@/components/Profiles";

export function ActiveRideCard({ ride }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const next = DRIVER_NEXT[ride.status];
  const sharing = useShareLocation(ride);
  const refresh = () => ["driverActive", "driverRides", "adminDashboard", "myRides"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const advance = async () => {
    setBusy(true);
    try {
      try {
        await api.post(`/rides/${ride.id}/status`, { status: next.status });
      } catch (e) {
        if (next.status !== "in_progress" || !/PIN/.test(formatApiError(e))) throw e;
        const pin = window.prompt("Enter the rider's 3-digit PIN");
        if (!pin) throw e;
        await api.post(`/rides/${ride.id}/status`, { status: next.status, pin: pin.trim() });
      }
      toast.success(next.status === "completed" ? `Ride completed · ${money(ride.fare)}` : next.label);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const cancel = async (reason) => {
    try {
      await api.post(`/rides/${ride.id}/driver-cancel`, { reason });
      toast("Ride cancelled");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="space-y-4 rounded-2xl border border-neon-lime/40 bg-card p-4" data-testid="active-ride-card">
      <div className="flex flex-wrap items-center gap-2"><SharingBadge on={sharing} /><MessagesButton rideId={ride.id} className="h-8 text-xs" /></div>
      <CounterpartProfile rideId={ride.id} />
      <RiderLocationForDriver rideId={ride.id} />
      <div className="flex items-center justify-between">
        <StatusBadge status={ride.status} />
        <span className="font-display text-2xl font-bold neon-text-lime" data-testid="active-ride-fare">{money(ride.fare)}</span>
      </div>
      <StepTrack status={ride.status} />
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-neon-violet font-bold text-white">{ride.rider_name?.[0]}</span>
        <div className="min-w-0">
          <p className="font-semibold" data-testid="active-ride-passenger">{ride.rider_name} · {ride.passengers} pax</p>
          {ride.rider_phone && <a href={`tel:${ride.rider_phone}`} className="flex items-center gap-1 text-xs text-neon-cyan"><Phone className="h-3 w-3" /> {ride.rider_phone}</a>}
        </div>
      </div>
      <Stops ride={ride} />
      <p className="text-xs text-muted-foreground">{when(ride.scheduled_at)} · {ride.quote?.service_window_minutes ?? ride.planned_window_minutes} min window · booking fee $0.00</p>
      {ride.notes && <p className="rounded-xl bg-muted px-3 py-2 text-sm">Note: {ride.notes}</p>}
      <div className="grid grid-cols-2 gap-2">
        <Button asChild variant="outline" className="rounded-full" data-testid="start-navigation-button">
          <a href={navUrl(ride)} target="_blank" rel="noreferrer"><Navigation className="mr-1 h-4 w-4" /> Navigate</a>
        </Button>
        {next && (
          <Button onClick={advance} disabled={busy} className="rounded-full" data-testid="driver-advance-status-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : next.label}
          </Button>
        )}
      </div>
      <button type="button" onClick={() => setCancelling(true)} className="w-full text-xs font-semibold text-muted-foreground hover:text-destructive" data-testid="driver-cancel-ride-button">Cancel ride</button>
      {cancelling && <ActionDialog title="Cancel this ride?" options={CANCEL_REASONS} cta="Cancel ride" onSubmit={cancel} onClose={() => setCancelling(false)} />}
    </div>
  );
}
